'use client';

import { useState } from 'react';
import { Checkbox, CheckboxOption } from './Checkbox';
import { ChangeStepFormEvent } from '../multi-step-form/multi-step-form.model';
import layoutStyles from '../multi-step-form/layout.module.css';

type Props = {
  options?: CheckboxOption[];
  columns?: number;
  name: string;
  value?: string[];
  onValueChange?: (selectedItems: string[]) => void;
  onChange: (value: ChangeStepFormEvent) => void;
  displayTextInput?: boolean;
  textInputValue?: string;
  textInputPlaceHolder?: string;
};

export const CheckboxGroup = ({
  value,
  onValueChange,
  onChange,
  options,
  columns = 1,
  name,
  displayTextInput,
  textInputValue,
  textInputPlaceHolder,
}: Props) => {
  const [selected, setSelected] = useState<string[]>(() => value ?? []);
  const [inputValue, setInputValue] = useState(() => textInputValue ?? '');
  const handleOnChange = (newValue: string) => {
    const selectedIndex = selected.indexOf(newValue);
    const selectedItems = [...selected];
    // if the item is already in the selected items means that the user is deselecting it
    // otherwise add it to selected items
    if (selectedIndex > -1) {
      selectedItems.splice(selectedIndex, 1);
    } else {
      selectedItems.push(newValue);
    }
    const newTextInputValue = !displayTextInput ? '' : textInputValue;
    const event: ChangeStepFormEvent = {
      target: {
        value: selectedItems,
        name,
        textInputValue: newTextInputValue,
      },
    };
    setInputValue(newTextInputValue ?? '');
    onChange(event);
    if (onValueChange) {
      onValueChange(selectedItems);
    }
    setSelected(selectedItems);
  };
  const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(() => e.target.value);
    onChange({
      target: { name, value: selected, textInputValue: e.target.value },
    });
  };
  return (
    <div className={`${layoutStyles.item} flex flex-col gap-3`}>
      <div
        className="grid gap-4"
        style={{
          gridTemplateColumns: `repeat(${columns},minmax(0,1fr))`,
        }}
      >
        {options?.map((option) => (
          <Checkbox
            key={option.value}
            option={option}
            checked={selected.includes(option.value)}
            onValueChange={handleOnChange}
            name={name}
          />
        ))}
      </div>
      {displayTextInput && (
        <input
          type="text"
          id={name}
          name={name}
          value={inputValue}
          placeholder={textInputPlaceHolder}
          onChange={handleTextInputChange}
          className="w-full p-4 rounded-md focus:outline-2 focus:outline-primary-green placeholder:text-[#707071] outline-[1.5px] outline-[#cfcfd0] outline"
        />
      )}
    </div>
  );
};
